import React, { useEffect, useRef, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Iter339 · Bug 13 — Persistent notifications panel with unread badge.
// Reads /notifications, shows the last 20 with a running unread count,
// clicking a row marks it read, and a "Mark all read" clears the badge.
// Rendered in the OPS header (also usable on any page).
export default function NotificationsPanel({ pollMs = 20000 }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/notifications?limit=20", { __suppress402: true });
      setItems(r.data?.items || []);
      setUnread(Number(r.data?.unread_count || 0));
    } catch {
      // Silent — panel is best-effort.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markOne = async (nid) => {
    setItems((prev) => prev.map((n) => n.id === nid ? { ...n, is_read: true } : n));
    setUnread((n) => Math.max(0, n - 1));
    try { await api.post(`/notifications/${nid}/read`, {}); } catch {}
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/mark-all-read", {});
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
      toast.success("All notifications marked read");
    } catch { toast.error("Couldn't mark all read"); }
  };

  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return "just now";
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
      return d.toLocaleDateString();
    } catch { return ""; }
  };

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="ops-notifications-toggle"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
        className="relative px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1"
      >
        <Bell className="w-3 h-3" />
        Alerts hub
        {unread > 0 && (
          <span
            data-testid="ops-notifications-unread-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF4500] text-white text-[9px] font-bold flex items-center justify-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="ops-notifications-panel"
          className="absolute right-0 mt-2 w-[320px] max-w-[90vw] bg-[#0A0A0A] border border-[#CCFF00] shadow-xl z-[60]"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626]">
            <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-bold">Notifications</div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                data-testid="ops-notifications-mark-all"
                className="text-[9px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00]"
              >
                Mark all read
              </button>
            )}
          </div>
          <div
            className="max-h-[60vh] overflow-y-auto"
            data-testid="ops-notifications-list"
          >
            {loading && items.length === 0 ? (
              <div className="p-4 text-[11px] text-[#A3A3A3] italic">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-4 text-[11px] text-[#A3A3A3] italic" data-testid="ops-notifications-empty">
                No notifications yet.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => !n.is_read && markOne(n.id)}
                  data-testid={`ops-notification-${n.id}`}
                  data-read={n.is_read ? "1" : "0"}
                  className={`w-full text-left px-3 py-2 border-b border-[#262626] hover:bg-[#141414] block ${n.is_read ? "opacity-60" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.is_read && (
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#CCFF00] shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-bold text-white truncate">{n.title || "Notification"}</div>
                      {n.body && <div className="text-[10px] text-[#A3A3A3] mt-0.5 whitespace-pre-wrap">{n.body}</div>}
                      <div className="text-[9px] text-[#525252] mt-1">{fmtTime(n.created_at)}</div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
